import React, { useEffect, useState } from "react";
import api from "@/lib/apiClient";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { TrendingUp, Clock, Wallet, CreditCard, Banknote, QrCode } from "lucide-react";

const PERIODS = [
  { key: "month", label: "Este mês" },
  { key: "30d", label: "Últimos 30 dias" },
  { key: "all", label: "Tudo" },
];
const brl = (v) => "R$ " + Number(v || 0).toFixed(2).replace(".", ",");
const METHOD_ICON = { Pix: QrCode, Dinheiro: Banknote, "Cartão": CreditCard };

export default function FinancialPanel() {
  const [period, setPeriod] = useState("month");
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    setLoading(true);
    api.get(`/metrics/financial?period=${period}`)
      .then(({ data }) => setData(data))
      .catch(() => setData(null))
      .finally(() => setLoading(false));
  }, [period]);

  return (
    <div className="space-y-4" data-testid="financial-panel">
      <div className="flex flex-wrap gap-2">
        {PERIODS.map((p) => (
          <Button key={p.key} size="sm" variant={period === p.key ? "default" : "outline"}
            className={`h-9 ${period === p.key ? "bg-blue-600 hover:bg-blue-700" : ""}`}
            onClick={() => setPeriod(p.key)} data-testid={`financial-period-${p.key}`}>
            {p.label}
          </Button>
        ))}
      </div>

      {loading ? (
        <p className="text-slate-400 text-sm">Carregando...</p>
      ) : !data ? (
        <p className="text-slate-400 text-sm">Não foi possível carregar o financeiro.</p>
      ) : (
        <>
          <div className="grid gap-4 sm:grid-cols-3">
            <StatCard icon={TrendingUp} color="emerald" label="Recebido"
              value={brl(data.total_pago)} sub={`${data.count_pago} OS pagas`} testid="fin-recebido" />
            <StatCard icon={Clock} color="amber" label="A receber"
              value={brl(data.total_pendente)} sub={`${data.count_pendente} pendentes`} testid="fin-a-receber" />
            <StatCard icon={Wallet} color="blue" label="Total no período"
              value={brl(data.total_geral)} sub={`${data.count_pago + data.count_pendente} OS`} testid="fin-total" />
          </div>

          <Card className="border-slate-200">
            <CardContent className="p-4">
              <h3 className="text-sm font-semibold text-slate-500 mb-3">Recebido por forma de pagamento</h3>
              <div className="grid gap-3 sm:grid-cols-3">
                {Object.entries(data.by_method).map(([m, v]) => {
                  const Icon = METHOD_ICON[m] || Wallet;
                  return (
                    <div key={m} className="flex items-center gap-3 rounded-lg border border-slate-100 bg-slate-50 p-3" data-testid={`fin-method-${m}`}>
                      <div className="h-9 w-9 rounded-md bg-white border border-slate-200 flex items-center justify-center">
                        <Icon className="h-4 w-4 text-[#1e7be0]" />
                      </div>
                      <div>
                        <p className="text-xs text-slate-500">{m}</p>
                        <p className="font-semibold text-slate-800">{brl(v)}</p>
                      </div>
                    </div>
                  );
                })}
              </div>
            </CardContent>
          </Card>
        </>
      )}
    </div>
  );
}

const COLORS = {
  emerald: "bg-emerald-50 text-emerald-700 border-emerald-100",
  amber: "bg-amber-50 text-amber-700 border-amber-100",
  blue: "bg-blue-50 text-blue-700 border-blue-100",
};

function StatCard({ icon: Icon, color, label, value, sub, testid }) {
  return (
    <Card className="border-slate-200" data-testid={testid}>
      <CardContent className="p-4 flex items-start justify-between">
        <div>
          <p className="text-xs text-slate-500">{label}</p>
          <p className="text-2xl font-bold text-slate-800 mt-1">{value}</p>
          <p className="text-xs text-slate-400 mt-0.5">{sub}</p>
        </div>
        <div className={`h-10 w-10 rounded-lg border flex items-center justify-center ${COLORS[color]}`}>
          <Icon className="h-5 w-5" />
        </div>
      </CardContent>
    </Card>
  );
}
